/** Author: Codex CLI Agent | Generation: combat timing instrumentation | Version: v3.2.x | Modified: 2025-11-24  
 * CombatTiming 工具：定义时长常量与估算函数 f(x,y)=本次战斗总时间（ms） */

export type CombatStepKind = 'damage'|'miss'|'death';

export interface CombatTimingState {
  combatId: string;
  openedAt: number;
  steps: Array<{ kind: CombatStepKind; t: number }>;
  segments?: Array<{ kind: CombatStepKind; start: number; end: number; dur: number }>;
  closedAt?: number;
}

// 可视节拍常量（前端自控，可按需要微调）
export const COMBAT_TIMING_CONST = {
  OPEN_OVERHEAD_MS: 120,     // 打开剧场与初始化 UI 的开销
  MIN_COMBAT_MS: 700,        // 战斗最小展示时长（与 Sequencer 的最小时长一致）
  PER_DAMAGE_MS: 260,        // 单段伤害的平均可视时长
  PER_MISS_MS: 220,          // 单段 Miss 的平均可视时长
  CLOSE_OVERHEAD_MS: 180     // 关闭剧场与清理收尾的开销
} as const;

/**
 * 估算函数：f(x,y)=本次战斗总时间（毫秒）
 * x=伤害段数，y=miss段数；返回值包含最小时长保护与开/关固定开销。
 */
export function estimateCombatDuration(damageCount: number, missCount: number, consts = COMBAT_TIMING_CONST): number {
  const base = consts.OPEN_OVERHEAD_MS + damageCount * consts.PER_DAMAGE_MS + missCount * consts.PER_MISS_MS;
  const body = Math.max(base, consts.MIN_COMBAT_MS);
  return body + consts.CLOSE_OVERHEAD_MS;
}

/** 将 state 计算为统计摘要与估算值 */
export function summarizeCombatTiming(state: CombatTimingState) {
  const openedAt = state.openedAt;
  const closedAt = state.closedAt ?? Date.now();
  const durationMs = Math.max(0, closedAt - openedAt);
  const damageCount = state.steps.filter(s => s.kind === 'damage').length;
  const missCount = state.steps.filter(s => s.kind === 'miss').length;
  const estimateMs = estimateCombatDuration(damageCount, missCount);
  return { openedAt, closedAt, durationMs, damageCount, missCount, estimateMs, diffMs: durationMs - estimateMs };
}

/** 记录单段步骤的起止与时长 */
export function pushSegment(state: CombatTimingState, kind: CombatStepKind, start: number, end: number) {
  if (!state.segments) state.segments = [];
  const dur = Math.max(0, end - start);
  state.segments.push({ kind, start, end, dur });
}

// 调试：导出到 window（可选）
export function attachTimingDebug() {
  if (typeof window !== 'undefined') {
    (window as any).__ABR_COMBAT_TIMING_CONST__ = COMBAT_TIMING_CONST;
    (window as any).estimateCombatDuration = estimateCombatDuration;
  }
}
